import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft'
import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import { ILearningProgram } from 'interfaces/roles'
import { AccessToLearningProgramsEnum } from 'enum/accessToLearningPrograms'
import { emptyLearningProgram } from 'store/slices/initialData'
import { useRolesAndPermissions } from '../../../../context/RolesAndPermissionsContext'
import { Description, BackFooterButton, BackFooterWrapper } from './styled'
import Options from './options'
import LearningPrograms from './learningPrograms'
import Details from './learningPrograms/details'

interface IProps {
  close: () => void
}

const AddLearningPrograms = ({ close }: IProps) => {
  const { t } = useTranslation()
  const prefix = 'configuration.rolesAndPermissions.form.addLearningPrograms'
  const {
    roleDetail: data,
    setAccessToLearningPrograms: updateAccessToLearningPrograms,
    addLearningPrograms: appendLearningPrograms,
    removeAllLearningPrograms,
  } = useRolesAndPermissions()
  const [accessToLearningProgram, setAccessToLearningProgram] = useState(
    AccessToLearningProgramsEnum.None
  )
  const [selectedLearningPrograms, setSelectedLearningPrograms] = useState<
    ILearningProgram[]
  >([])
  const [viewingProgramCourses, setViewingProgramCourses] =
    useState<ILearningProgram | null>(null)

  useEffect(() => {
    setAccessToLearningProgram(data.accessToLearningPrograms)
    setSelectedLearningPrograms([emptyLearningProgram])
  }, [])

  const next = () => {
    updateAccessToLearningPrograms(accessToLearningProgram)
    if (accessToLearningProgram === AccessToLearningProgramsEnum.Specific) {
      appendLearningPrograms(selectedLearningPrograms)
    } else {
      removeAllLearningPrograms()
    }
    close()
  }

  const changeOption = (value: AccessToLearningProgramsEnum) => {
    setAccessToLearningProgram(value)
    setSelectedLearningPrograms([emptyLearningProgram])
  }

  const backToProgramsList = () => {
    setViewingProgramCourses(null)
  }

  const isCourseDetailView = viewingProgramCourses !== null

  return (
    <Modal
      isOpen={true}
      title={
        isCourseDetailView
          ? t(`${prefix}.programTitle`, {
              name: viewingProgramCourses.name,
            })
          : t(`${prefix}.title`)
      }
      maxwidth={800}
      maxheight={660}
      close={close}
      onBack={isCourseDetailView ? backToProgramsList : undefined}
      actions={
        isCourseDetailView ? (
          <BackFooterWrapper>
            <BackFooterButton type="button" onClick={backToProgramsList}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {t(`${prefix}.actions.back`)}
            </BackFooterButton>
          </BackFooterWrapper>
        ) : (
          <ModalButtons
            primaryText={t(`${prefix}.actions.select`)}
            primaryAction={next}
            secondaryText={t(`${prefix}.actions.cancel`)}
            secondaryAction={close}
            disablePrimaryButton={
              accessToLearningProgram ===
                AccessToLearningProgramsEnum.Specific &&
              selectedLearningPrograms.filter((item) => item.id === 0).length >
                0
            }
          />
        )
      }
    >
      {isCourseDetailView ? (
        <Details id={viewingProgramCourses.id} />
      ) : (
        <>
          <Description>{t(`${prefix}.description`)}</Description>
          <Options
            activeOption={accessToLearningProgram}
            changeOption={changeOption}
          />
          {accessToLearningProgram ===
            AccessToLearningProgramsEnum.Specific && (
            <LearningPrograms
              selectedLearningPrograms={selectedLearningPrograms}
              setSelectedLearningPrograms={setSelectedLearningPrograms}
              onViewCourses={setViewingProgramCourses}
            />
          )}
        </>
      )}
    </Modal>
  )
}

export default AddLearningPrograms
